import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { tenantApi } from '../../api/endpoints/tenant';
import { handleApiError } from '../../api/errorMapping';
import { generateIdempotencyKey } from '../../lib/idempotency';
import { Gate } from '../permissions/Gate';
import { Button } from '../ui/button';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { displayNameOrShort } from '../../lib/displayName';
import { RoleScopeFields, type RoleScopeSelection } from './RoleScopeFields';
import type { TenantUserDetailLocal } from '../../api/types/TenantLocal';
import type { RoleAssignmentInfoLocal } from '../../api/types/MeLocal';
import type { Scope } from '../../api/types/permissions';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Tenant donde viven las asignaciones (drill-down platform o el del actor en retailer). */
    tenantId: string;
    cognitoSub: string;
    /** Etiqueta legible del usuario (email/nombre) para el encabezado. */
    userLabel: string;
    /** Fetcher del detalle del usuario (devuelve roleAssignments). Difiere por contexto:
     *  tenant → `tenantApi.getUser(tid, sub)`; platform → `platformApi.getTenantUser(tenantId, sub)`. */
    fetchDetail: () => Promise<TenantUserDetailLocal>;
    /** queryKey del DETALLE (para invalidar tras asignar/revocar). */
    detailQueryKey: QueryKey;
    /** queryKey del LISTADO de usuarios (para invalidar tras asignar/revocar). */
    listQueryKey: QueryKey;
    /**
     * Scope/scopeRef con que se gatean los permisos `tenant.role.assign`/`tenant.role.revoke` en la
     * UI. Difiere por contexto: platform → TENANT/tenantId; retailer → RETAILER/retailerId (el actor
     * opera a nivel retailer, no cubre TENANT por rank). El backend re-enforza en ambos casos.
     */
    gateScope: Scope;
    gateScopeRef: string;
}

/** "TENANT" / "PLATFORM" / "LEVEL_1: Sucursal Centro". */
function scopeLabel(a: RoleAssignmentInfoLocal): string {
    if (a.scope === 'PLATFORM' || a.scope === 'TENANT') return a.scope;
    // DEC-013: el backend pobla `scopeRefName` con el scopeRef crudo (UUID) cuando no tiene el nombre.
    // `displayNameOrShort` muestra el nombre legible o, en su defecto, el id acortado — NUNCA el UUID.
    const dest = displayNameOrShort(a.scopeRefName, a.scopeRef);
    return `${a.scope}: ${dest}`;
}

/**
 * Gestión COMPLETA de roles de un usuario: ver actuales / agregar / quitar. Genérico y controlado —
 * sirve al drill-down platform y al de retailer (el contexto inyecta `fetchDetail` + las queryKeys).
 *
 *  - "Roles actuales": lista cada `roleAssignment` (rol + scope legible) con un botón "Quitar"
 *    (revoke) gateado por `tenant.role.revoke`. Necesita el `assignmentId` para revocar.
 *  - "Agregar rol": reusa `RoleScopeFields` (selector rol→scope→scopeRef agrupado por nivel,
 *    compartido con `RoleAssignmentDialog`). Asigna uno o varios scopeRef con `Promise.allSettled`.
 *
 * Overlay inline (no Radix Dialog) — patrón anti-freeze del repo: cierra bien, sin overlays colgados.
 */
export function ManageUserRolesDialog({
    open,
    onOpenChange,
    tenantId,
    cognitoSub,
    userLabel,
    fetchDetail,
    detailQueryKey,
    listQueryKey,
    gateScope,
    gateScopeRef,
}: Props) {
    const qc = useQueryClient();
    const [selection, setSelection] = useState<RoleScopeSelection | null>(null);
    // assignmentId en curso de revocación (para deshabilitar solo ese botón).
    const [revokingId, setRevokingId] = useState<string | null>(null);

    const { data: detail, isLoading } = useQuery({
        queryKey: detailQueryKey,
        queryFn: fetchDetail,
        enabled: open,
    });

    const invalidate = async (): Promise<void> => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: detailQueryKey }),
            qc.invalidateQueries({ queryKey: listQueryKey }),
        ]);
    };

    const assignMut = useMutation({
        mutationFn: async () => {
            if (!selection) {
                throw new Error('selección requerida');
            }
            const { roleId, roleScope, refsToAssign } = selection;
            const results = await Promise.allSettled(
                refsToAssign.map((scopeRef) =>
                    tenantApi.assignRole(
                        tenantId,
                        { cognitoSub, roleId, scope: roleScope, scopeRef },
                        { idempotencyKey: generateIdempotencyKey() },
                    ),
                ),
            );
            const ok = results.filter((r) => r.status === 'fulfilled').length;
            const failed = results.filter(
                (r): r is PromiseRejectedResult => r.status === 'rejected',
            );
            return { ok, failed, total: refsToAssign.length };
        },
        onSuccess: async ({ ok, failed, total }) => {
            if (ok > 0) {
                toast.success(`Rol asignado a ${String(ok)} destino(s)`);
            }
            if (failed.length > 0) {
                if (ok > 0) {
                    toast.error(
                        `${String(failed.length)} de ${String(total)} asignacion(es) fallaron`,
                    );
                }
                handleApiError(failed[0].reason);
            }
            await invalidate();
        },
        onError: handleApiError,
    });

    const revokeMut = useMutation({
        mutationFn: (assignmentId: string) =>
            tenantApi.revokeRole(tenantId, assignmentId, cognitoSub),
        onMutate: (assignmentId: string) => {
            setRevokingId(assignmentId);
        },
        onSuccess: async () => {
            toast.success('Rol quitado');
            await invalidate();
        },
        onError: handleApiError,
        onSettled: () => {
            setRevokingId(null);
        },
    });

    if (!open) return null;

    const assignments = detail?.roleAssignments ?? [];

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
            <button
                type="button"
                aria-label="Cerrar"
                className="fixed inset-0 bg-black/50"
                onClick={() => {
                    onOpenChange(false);
                }}
            />
            <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                <div className="mb-1 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Gestionar roles</h2>
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        ✕
                    </button>
                </div>
                <p className="mb-4 text-sm text-muted-foreground">{userLabel}</p>

                {/* ── Roles actuales ── */}
                <div className="space-y-2">
                    <p className="inline-flex items-center gap-1.5 text-sm font-medium">
                        Roles actuales
                        <HelpHint text={HELP_TEXT.roleAssignment} />
                    </p>
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">Cargando…</p>
                    ) : assignments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Sin roles asignados</p>
                    ) : (
                        <ul className="space-y-1">
                            {assignments.map((a) => (
                                <li
                                    key={a.assignmentId || `${a.roleId}-${a.scope}-${a.scopeRef}`}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-input px-3 py-2 text-sm"
                                >
                                    <span className="flex flex-wrap items-center gap-2">
                                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                                            {a.roleName}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {scopeLabel(a)}
                                        </span>
                                    </span>
                                    <Gate
                                        permission="tenant.role.revoke"
                                        scope={gateScope}
                                        scopeRef={gateScopeRef}
                                    >
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            aria-label={`Quitar ${a.roleName}`}
                                            // Sin assignmentId no se puede revocar (el backend lo
                                            // identifica por ese id en el path) → botón inhabilitado.
                                            disabled={
                                                !a.assignmentId ||
                                                (revokeMut.isPending &&
                                                    revokingId === a.assignmentId)
                                            }
                                            onClick={() => {
                                                if (a.assignmentId) {
                                                    revokeMut.mutate(a.assignmentId);
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                            <span className="ml-1">Quitar</span>
                                        </Button>
                                    </Gate>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* ── Agregar rol ── */}
                <Gate permission="tenant.role.assign" scope={gateScope} scopeRef={gateScopeRef}>
                    <div className="mt-6 border-t pt-4">
                        <p className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium">
                            Agregar rol
                            <HelpHint text={HELP_TEXT.roleAssignment} />
                        </p>
                        <RoleScopeFields
                            tenantId={tenantId}
                            open={open}
                            onChange={setSelection}
                            idPrefix="murd"
                        />
                        <div className="mt-4 flex justify-end">
                            <Button
                                onClick={() => {
                                    assignMut.mutate();
                                }}
                                disabled={selection === null || assignMut.isPending}
                            >
                                Agregar rol
                            </Button>
                        </div>
                    </div>
                </Gate>

                <div className="mt-6 flex justify-end gap-2 border-t pt-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
}
